import React, { useEffect, useState, useRef } from 'react';
import { apiService } from '../../../components/Config/api.service';
import SecondarySidebar from '../../../components/SideBar/SecondarySidebar';
import { FaUser, FaSortAlphaDown, FaSortAlphaUp, FaFilter } from 'react-icons/fa';
import Select from 'react-select';

const EmployeeNav = ({
    setPdfUrl, selectedMonth,
    setReport, setIsloading, setEmployeeId, setContractId, setFilteredEmployees,
    setIsMonthClosed, refreshTrigger
}) => {
    const [employees, setEmployees] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(selectedMonth || new Date().toISOString().slice(0, 7)); // Format: YYYY-MM
    const [sortOrder, setSortOrder] = useState('asc');
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [showFilterPopover, setShowFilterPopover] = useState(false);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const filterBtnRef = useRef(null);
    const popoverRef = useRef(null);
    const categoryOptions = [
        ...categories.map(cat => ({
            value: cat.id,
            label: cat.name
        }))
    ];

    useEffect(() => {
        apiService.getElement('/employee/categories')
            .then(res => {
                setCategories(res.data.categories || []);
            })
            .catch(() => setCategories([]));
    }, []);

    const fetchEmployees = async (month = currentMonth) => {
        try {
            // Préparer les paramètres pour le nouveau point de terminaison unifié
            let params = [
                `offset=${offset}`,
                `limit=${limit}`,
                `month=${month}`,
                `sort_by=name`,
                `sort_dir=${sortOrder}`
            ];
            if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
            if (selectedCategories.length > 0) params.push(`categories=${selectedCategories.join(',')}`);

            // Utiliser le nouveau point de terminaison optimisé
            const response = await apiService.getElement(`/month_overview?${params.join('&')}`);
            const { employees: fetchedEmployees, pagination, is_month_closed } = response.data;

            // Mettre à jour l'état de clôture du mois dans le composant parent
            if (setIsMonthClosed) {
                setIsMonthClosed(is_month_closed || false);
            }

            if (fetchedEmployees && fetchedEmployees.length > 0) {
                setEmployees(fetchedEmployees);
                setFilteredEmployees(fetchedEmployees);
                setTotalCount(pagination?.total_count || 0);
                setTotalPages(pagination?.total_pages || 1);

                // L'état des bulletins est maintenant directement dans l'objet 'employee',
                // il n'est plus nécessaire de le gérer dans un état séparé.

                // Activer le premier employé si aucun n'est sélectionné ou si le mois a changé
                if (!activeTab || month !== currentMonth) {
                    setIsloading(true);
                    const firstEmployee = fetchedEmployees[0];
                    console.log('First employee:', firstEmployee);
                    if (activeTab && activeTab === firstEmployee.id) {
                        if (firstEmployee.payslip_id) {
                            handleDownloadPDF({
                                id: firstEmployee.payslip_id,
                                State: firstEmployee.payslip_state,
                                Employee: firstEmployee.name,
                                email: firstEmployee.email
                            });
                        } else {
                            clearPayslipDisplay(firstEmployee.id);
                        }
                    }else{
                        setActiveTab(firstEmployee.id);
                        if (!firstEmployee.payslip_id) {
                           setIsloading(false);
                        }
                    }
                }
            } else {
                setEmployees([]);
                setFilteredEmployees([]);
                setTotalCount(0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('There was an error fetching month overview!', error);
            setErrorMessage('Erreur lors du chargement des employés.');
        }
    };

    useEffect(() => {        
        setCurrentMonth(selectedMonth);        
        fetchEmployees(selectedMonth);
        // eslint-disable-next-line
    }, [searchQuery, selectedCategories, sortOrder, offset, limit, selectedMonth, refreshTrigger]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Cette fonction n'a plus besoin de faire d'appel API pour trouver le bulletin
    const findPayslipForEmployee = async (employeeId) => {
        setIsloading(true);
        const employee = employees.find(emp => emp.id === employeeId);

        if (employee && employee.payslip_id) {
            // Utiliser l'ID du bulletin déjà récupéré pour télécharger le PDF
            handleDownloadPDF({ 
                id: employee.payslip_id, 
                State: employee.payslip_state, 
                Employee: employee.name,
                email: employee.email
            });
        } else {
            // Aucun bulletin n'existe pour cet employé ce mois-ci
            clearPayslipDisplay(employeeId);
        }
    };

    const clearPayslipDisplay = (employeeId) => {
        setPdfUrl('');
        const employeeName = employees.find(emp => emp.id === employeeId)?.name || '';
        setReport({
            Employee: employeeName,
            Ref: 'Aucun bulletin disponible',
            De: '',
            Vers: ''
        });
        setIsloading(false);
    };

    const handleDownloadPDF = (item) => {
        apiService.getBlobFile(`/payslip/pdf/${item.id}`)
            .then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                setPdfUrl(url);
                setReport(item);
            })
            .catch(error => {
                console.error('Error downloading PDF:', error);
                setErrorMessage('Erreur lors du téléchargement du PDF.');
                clearPayslipDisplay(activeTab);
            })
    };

    const sidebarItems = employees.map(employee => ({
        id: employee.id,
        label: employee.name,
        icon: <FaUser className="w-5 h-5 text-gray-400" />,
        // Utiliser directement la propriété de l'objet employé
        payslipState: employee.payslip_state
    }));

    const handleTabChange = (employeeId) => {
        setActiveTab(employeeId);
        setEmployeeId(employeeId);
        console.log('Active tab changed to:', employeeId);
        setReport({
            Employee: employees.find(emp => emp.id === employeeId)?.name || '',
            Ref: '',
            De: '',
            Vers: ''
        });

        const selectedEmployee = employees.find(emp => emp.id === employeeId);
        console.log('Selected employee:', selectedEmployee);
        if (selectedEmployee && selectedEmployee.contract_id) {
            setContractId(selectedEmployee.contract_id);
            console.log('Selected contract ID:', selectedEmployee.contract_id);
        }
        // L'appel à findPayslipForEmployee est maintenant géré par le useEffect sur activeTab
    };

    // Pagination controls
    const handlePrevPage = () => {
        if (offset - limit >= 0) setOffset(offset - limit);
    };
    const handleNextPage = () => {
        if (offset + limit < totalCount) setOffset(offset + limit);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target) &&
                !filterBtnRef.current.contains(event.target)
            ) {
                setShowFilterPopover(false);
            }
        }
        if (showFilterPopover) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showFilterPopover]);

    useEffect(() => {
        if (activeTab) {
            // La dépendance à currentMonth n'est plus nécessaire
            findPayslipForEmployee(activeTab);
        }
        // eslint-disable-next-line
    }, [activeTab]);

    return (
        <div className="min-h-screen">
            <div className="mb-4 bg-white rounded-lg p-4 pb-0">

                <div className="flex items-center mb-2 gap-2 relative">
                    <input
                        type="text"
                        placeholder="Rechercher un employé..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="border-gray-200 border py-2 px-4 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                    <button
                        ref={filterBtnRef}
                        className="py-3 px-2 rounded hover:bg-gray-100 border border-gray-200"
                        onClick={() => setShowFilterPopover(v => !v)}
                        title="Filtres"
                    >
                        <FaFilter />
                    </button>
                    {/* Popover */}
                    {showFilterPopover && (
                        <div
                            ref={popoverRef}
                            className="absolute right-0 top-12 z-50 bg-white rounded-lg shadow-lg p-4 min-w-[220px] border"
                        >
                            <div className="mb-4">
                                <label className="block font-semibold mb-1">Trier par nom</label>
                                <div className="flex gap-3">
                                    <button
                                        className={`flex items-center gap-1 px-2 py-1 rounded ${sortOrder === 'asc' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100'}`}
                                        onClick={() => {
                                            setSortOrder('asc');
                                            setShowFilterPopover(false);
                                        }}
                                    >
                                        <FaSortAlphaUp /> A-Z
                                    </button>
                                    <button
                                        className={`flex items-center gap-1 px-2 py-1 rounded ${sortOrder === 'desc' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100'}`}
                                        onClick={() => {
                                            setSortOrder('desc');
                                            setShowFilterPopover(false);
                                        }}
                                    >
                                        <FaSortAlphaDown /> Z-A
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Etiquette</label>
                                <Select
                                    isMulti
                                    options={categoryOptions}
                                    value={categoryOptions.filter(opt => selectedCategories.includes(String(opt.value)))}
                                    onChange={options => setSelectedCategories(options.map(opt => String(opt.value)))}
                                    className="w-full text-sm"
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="text-xs text-end text-gray-500 mt-1">
                    {employees.length} employé(s) trouvé(s)
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                    {selectedCategories.length > 0 && selectedCategories.map(catId => {
                        const cat = categories.find(c => String(c.id) === String(catId));
                        return (
                            <span key={catId} className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs flex items-center gap-1">
                                {cat?.name}
                                <button
                                    className="ml-1 text-orange-500 hover:text-orange-700"
                                    onClick={() => setSelectedCategories(selectedCategories.filter(id => id !== catId))}
                                    title="Retirer cette étiquette"
                                >×</button>
                            </span>
                        );
                    })}
                </div>
            </div>
            {employees.length > 0 ? (
                <div className="relative h-[calc(100vh-200px)] flex flex-col overflow-y-auto">
                    <div className="flex-1">
                        <SecondarySidebar
                            menuItems={sidebarItems}
                            setActiveTab={handleTabChange}
                            activeTab={activeTab}
                        />
                    </div>
                    {/* Pagination controls sticky */}
                    <div className="bg-white pb-4 flex justify-center items-center">
                        <div className="join">
                            <button
                                className="join-item btn btn-sm"
                                onClick={handlePrevPage}
                                disabled={offset === 0}
                                style={{ opacity: offset === 0 ? 0.5 : 1, cursor: offset === 0 ? 'not-allowed' : 'pointer' }}
                            >
                                «
                            </button>
                            <button className="join-item btn btn-sm cursor-default">
                                Page {Math.floor(offset / limit) + 1} / {totalPages}
                            </button>
                            <button
                                className="join-item btn btn-sm"
                                onClick={handleNextPage}
                                disabled={offset + limit >= totalCount}
                                style={{ opacity: offset + limit >= totalCount ? 0.5 : 1, cursor: offset + limit >= totalCount ? 'not-allowed' : 'pointer' }}
                            >
                                »
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] text-gray-500">
                    Aucun employé avec contrat actif trouvé.
                </div>
            )}
            {errorMessage && <div className="text-red-600 mt-4">{errorMessage}</div>}
        </div>
    );
};
export default EmployeeNav;